/**
 * Empty, error and blocked states.
 *
 * An empty list says what to do next. An error says what happened in plain
 * words. A quota denial or a suppression block is neither of those: it is the
 * platform doing its job, so it reads calmly and explains which limit was hit
 * and when it clears.
 */
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import { describeClearsAt, describeError, errorHeading } from '../lib/errors'
import type { ErrorDescription } from '../lib/errors'
import { Button, LinkButton } from './Button'
import { Card } from './Card'
import { FeaturedIcon } from './FeaturedIcon'
import { IconAlert, IconBlocked, IconInfo, IconOffline, IconRefresh } from './icons'

export interface EmptyStateProps {
  title: ReactNode
  /** Say what to do next. Never "No data". */
  description?: ReactNode
  icon?: ReactNode
  action?: ReactNode
  className?: string
  size?: 'sm' | 'md'
  /**
   * `card` is the full empty state: the glyph tile in its rings, a title, a
   * line and an action, at least 320 tall in a card body. `list` is the one
   * quiet line a list shows when a filter leaves nothing (`Nothing there yet`),
   * and it draws the title as that line. Left out, the empty state is drawn as
   * a card without the minimum height, which is what every existing call site
   * had room for.
   */
  variant?: 'card' | 'list'
  /**
   * When the emptiness has a precondition, the place that fixes it. The
   * description names the precondition and this link is the way there. It is
   * drawn as the action when no other action was given.
   */
  fixHref?: string
  /** The words on the fix link, such as `Add contacts`. */
  fixLabel?: string
  /**
   * For the `list` variant: draw the icon in its rings above the line, with
   * the description and the action under it. A list pane that is the only
   * thing on screen (the inbox list, say) is too bare as one line, and a card
   * is too loud for an emptiness the reader made with a filter. Without it the
   * list variant stays the one quiet line.
   */
  rings?: boolean
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
  size = 'md',
  variant,
  fixHref,
  fixLabel,
  rings = false,
}: EmptyStateProps) {
  if (variant === 'list' && rings) {
    const listNext =
      action ??
      (fixHref === undefined ? null : (
        <LinkButton to={fixHref} variant="secondary" size="sm">
          {fixLabel ?? 'Fix this'}
        </LinkButton>
      ))
    return (
      <div
        data-empty="list"
        className={cn('relative isolate flex flex-col items-center justify-center overflow-hidden px-6 py-8 text-center', className)}
      >
        {icon ? <FeaturedIcon icon={icon} variant="outline-square" size="lg" rings /> : null}
        <div className={cn('max-w-[22rem]', icon ? 'mt-4' : null)}>
          <p className="text-base text-ink-muted">{title}</p>
          {description ? <p className="mt-1 text-base text-ink-muted">{description}</p> : null}
        </div>
        {listNext ? <div className="mt-4">{listNext}</div> : null}
      </div>
    )
  }

  if (variant === 'list') {
    return (
      <p data-empty="list" className={cn('px-6 py-8 text-center text-base text-ink-muted', className)}>
        {title}
      </p>
    )
  }

  const compact = size === 'sm'
  const fix =
    action === undefined && fixHref !== undefined ? (
      <LinkButton to={fixHref} variant="secondary" size="md">
        {fixLabel ?? 'Fix this'}
      </LinkButton>
    ) : null
  const next = action ?? fix

  return (
    <div
      data-empty={variant ?? 'card'}
      className={cn(
        'relative isolate flex flex-col items-center justify-center overflow-hidden text-center',
        compact ? 'px-4 py-8' : 'p-8',
        variant === 'card' && 'min-h-[20rem]',
        className,
      )}
    >
      {icon ? (
        <FeaturedIcon
          icon={icon}
          variant="outline-square"
          size={compact ? 'md' : 'lg'}
          // The rings are 192 across, which a compact slot has no room for.
          rings={!compact}
        />
      ) : null}
      <div className={cn('max-w-[22rem]', icon ? 'mt-4' : null)}>
        <p className={cn('font-semibold text-ink', compact ? 'text-base' : 'text-title')}>{title}</p>
        {description ? <p className="mt-1 text-base text-ink-muted">{description}</p> : null}
      </div>
      {next ? <div className={compact ? 'mt-4' : 'mt-6'}>{next}</div> : null}
    </div>
  )
}

export interface HeroEmptyProps {
  title: ReactNode
  /** Three lines at most: what this page is for and what setting it up gives you. */
  description: ReactNode
  /** One primary `lg` button, the setup step. */
  action: ReactNode
  /**
   * A picture of this product's own screen in use, from `public/empty/`.
   * Never an advert for something else. Hidden below 1024px.
   */
  image?: { src: string; alt: string }
  className?: string
}

/**
 * A whole page with nothing on it yet, where the next step is a setup
 * (Mailboxes, Offers, Knowledge). Two columns from 1024px: the words and the
 * one button on the left, the screen in use on the right, cropped by the edge
 * of the pane.
 */
export function HeroEmpty({ title, description, action, image, className }: HeroEmptyProps) {
  return (
    <div
      data-empty="hero"
      className={cn('grid items-center gap-12 overflow-hidden py-12 lg:grid-cols-2', className)}
    >
      <div className="max-w-[32.5rem]">
        <h2 className="text-display-md font-semibold text-ink">{title}</h2>
        <p className="mt-4 line-clamp-3 text-title text-ink-muted">{description}</p>
        <div className="mt-8 [&>*]:min-w-[12.5rem]">{action}</div>
      </div>
      {image ? (
        <div className="relative hidden lg:block">
          <img
            src={image.src}
            alt={image.alt}
            className="w-[140%] max-w-none rounded-xl border border-line shadow-xl"
          />
        </div>
      ) : null}
    </div>
  )
}

const TONE_STYLES: Record<ErrorDescription['tone'], { wrap: string; icon: ReactNode }> = {
  blocked: {
    wrap: 'border-warning/30 bg-warning-soft',
    icon: <IconBlocked className="h-5 w-5 text-warning-text" />,
  },
  invalid: {
    wrap: 'border-warning/30 bg-warning-soft',
    icon: <IconInfo className="h-5 w-5 text-warning-text" />,
  },
  fault: {
    wrap: 'border-danger/30 bg-danger-soft',
    icon: <IconAlert className="h-5 w-5 text-danger-text" />,
  },
  offline: {
    wrap: 'border-line bg-surface-muted',
    icon: <IconOffline className="h-5 w-5 text-ink-muted" />,
  },
}

export interface ErrorStateProps {
  error: unknown
  /** Overrides the derived heading when the page has better context. See `errorHeading` for the one code it does not override. */
  title?: ReactNode
  onRetry?: () => void
  retryLabel?: string
  className?: string
  /** Compact form for inline placement inside a card or a form. */
  inline?: boolean
  children?: ReactNode
}

/**
 * The one component every page uses to render a failed request. It reads the
 * error code, not the message, so a quota denial never looks like a crash.
 */
export function ErrorState({
  error,
  title,
  onRetry,
  retryLabel = 'Try again',
  className,
  inline = false,
  children,
}: ErrorStateProps) {
  const described = describeError(error)
  const styles = TONE_STYLES[described.tone]
  const clears = describeClearsAt(described.clearsAt)

  return (
    <div
      role="alert"
      className={cn('rounded-md border px-3.5 py-3', styles.wrap, inline ? 'text-sm' : 'text-sm', className)}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">{styles.icon}</span>
        <div className="min-w-0 flex-1 space-y-1.5">
          <p className="font-semibold text-ink">{errorHeading(described, title)}</p>
          <p className="leading-relaxed text-ink-muted">{described.detail}</p>

          {clears ? (
            <p className="text-xs text-ink-muted">
              <span className="font-medium">Clears</span> {clears}
            </p>
          ) : null}

          {described.issues?.length ? (
            <ul className="mt-1 space-y-1 text-xs text-ink-muted">
              {described.issues.map((issue, index) => (
                <li key={index} className="flex gap-2">
                  <span className="font-mono text-ink-subtle">
                    {issue.line !== undefined ? `line ${issue.line}` : issue.path || '—'}
                  </span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          ) : null}

          {children}

          {onRetry && described.retryable ? (
            <div className="pt-1">
              <Button size="sm" variant="secondary" onClick={onRetry} leadingIcon={<IconRefresh className="h-3.5 w-3.5" />}>
                {retryLabel}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

/** Full-panel version, for when the whole page failed to load. */
export function ErrorPanel(props: ErrorStateProps) {
  return (
    <Card className="overflow-hidden">
      <ErrorState {...props} className="border-0 bg-transparent" />
    </Card>
  )
}

/** Centred spinner block for a route or panel that is still loading. */
export function LoadingState({ label = 'Loading', className }: { label?: string; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 px-6 py-16', className)} aria-live="polite">
      <span className="h-6 w-6 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
      <p className="text-sm text-ink-subtle">{label}</p>
    </div>
  )
}
